import z from 'zod'
import {
  createContext,
  type SetStateAction,
  type Dispatch,
  useMemo,
  useState,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useContext,
} from 'react'
import { type UseDisclosureReturnValue } from '@mantine/hooks'

type SidebarNavigationContextType = UseDisclosureReturnValue

export const SidebarNavigationContext = createContext<SidebarNavigationContextType | null>(null)

export const SidebarNavigationProvider = ({ children }: PropsWithChildren): ReactNode => {
  const sidebar = useSidebarState()
  return <SidebarNavigationContext.Provider value={sidebar}>{children}</SidebarNavigationContext.Provider>
}

export const useSidebarNavigationContext = (): SidebarNavigationContextType => {
  const context = useContext(SidebarNavigationContext)
  if (!context) throw new Error('useSidebarNavigationContext must be used within a SidebarNavigationProvider')
  return context
}

const KEY = 'APP_SIDEBAR_STATE'

const useSidebarState = (): UseDisclosureReturnValue => {
  const [isOpen, _setIsOpen] = useState(() => {
    const stored = window.localStorage.getItem(KEY)
    if (stored) {
      const parseResult = z.boolean().safeParse(JSON.parse(stored))
      if (parseResult.success) return parseResult.data
    }
    return true
  })

  const setIsOpen: Dispatch<SetStateAction<boolean>> = useCallback(
    (input) => {
      _setIsOpen((prev) => {
        const newValue = input instanceof Function ? input(prev) : input
        window.localStorage.setItem(KEY, JSON.stringify(newValue))
        return newValue
      })
    },
    [_setIsOpen]
  )

  const handlers = useMemo(
    () => ({
      toggle: () => setIsOpen((prev) => !prev),
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
    }),
    [setIsOpen]
  )

  return useMemo(() => [isOpen, handlers], [isOpen, handlers])
}
